import { TFunction } from 'i18next';
import { action, makeObservable, observable, toJS } from 'mobx';
import { cast, getSnapshot, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { LocatorType } from '../utils/locatorTypes';
import { textValidationPattern } from '../utils/validationPatterns/inputValidationPattern';
import { ServerValidationStatus } from '../utils/validationStatusEnum';

export enum LoginType {
  noLogin = 'noLogin',
  token = 'token',
  password = 'username/password'
}

interface IServerInfoProps {
  url: string;
  loginType: LoginType;
  username?: string;
  password?: string;
  token?: string;
  validationStatus: ServerValidationStatus;
}

export interface IServerInfo extends IServerInfoProps {
  setValues: (changedValues: Partial<IServerInfoProps>) => void;
}

export interface ILocator {
  url: string;
  locatorType: LocatorType;
  useInMap: boolean;
}

export interface IImage {
  type?: string;
  content?: string;
  name?: string;
  uid?: string;
  status?: string;
}

export interface IGeneralSettingsProperties {
  municipalityName?: string;
  municipalityIcon?: IImage;
  municipalityLogo?: IImage;
  serverInfos: IServerInfoProps[];
  locators: ILocator[];
  portalUrl?: string;
  appId?: string;
  hideToken?: boolean;
}

export interface IGeneralSettings extends IGeneralSettingsProperties {
  serverInfos: IServerInfo[];
  hasChanges: boolean;
  setValues: (changedValues: Partial<IGeneralSettingsProperties>) => void;
  setMunicipalityName: (name: string) => void;
  setMunicipalityLogo: (logo?: IImage) => void;
  setMunicipalityIcon: (icon?: IImage) => void;
  setServerInfos: (serverInfos?: IServerInfo[]) => void;
  setLocators: (locators?: ILocator[]) => void;
  getLocatorsForMap: (t: TFunction) => { url: string; name: string }[];
  setHideToken: (setValue: boolean) => void;
  updatePortalUrl: (newUrl: string) => void;
  updateAppId: (newAppId: string) => void;
  setHasChanges: (value: boolean) => void;
  toJSON: () => IGeneralSettingsProperties;
}

class ServerInfo implements IServerInfo {
  url: string = '';
  loginType: LoginType = LoginType.noLogin;
  username?: string;
  password?: string;
  token?: string;
  validationStatus: ServerValidationStatus = ServerValidationStatus.notValidated;

  constructor(options?: IServerInfoProps) {
    Object.assign(this, options);
    makeObservable(this, {
      url: observable,
      loginType: observable,
      username: observable,
      password: observable,
      token: observable,
      validationStatus: observable,
      setValues: action
    });
  }

  public setValues(changedValues: Partial<IServerInfoProps>) {
    for (const key in changedValues) {
      this[key] = changedValues[key];
    }
  }
}

export class GeneralSettingsMobxModel implements IGeneralSettings {
  municipalityName?: string;
  municipalityIcon?: IImage;
  municipalityLogo?: IImage;
  serverInfos: IServerInfo[] = [];
  locators: ILocator[] = [];
  portalUrl?: string;
  appId?: string;
  hideToken?: boolean;
  hasChanges = false;

  constructor(options?: IGeneralSettingsProperties) {
    if (options) {
      const { serverInfos, ...rest } = options;
      Object.assign(this, rest);
      serverInfos.forEach((serverInfo) => this.serverInfos.push(new ServerInfo(serverInfo)));
    }
    makeObservable(this, {
      municipalityName: observable,
      municipalityIcon: observable,
      municipalityLogo: observable,
      serverInfos: observable,
      locators: observable,
      portalUrl: observable,
      appId: observable,
      hideToken: observable,
      hasChanges: observable,
      setValues: action,
      setMunicipalityName: action,
      setMunicipalityLogo: action,
      setMunicipalityIcon: action,
      setServerInfos: action,
      setLocators: action,
      setHideToken: action,
      updatePortalUrl: action,
      updateAppId: action,
      setHasChanges: action
    });
  }

  public setValues(changedValues: Partial<IGeneralSettingsProperties>) {
    for (const key in changedValues) {
      if (typeof this[key] === 'object') {
        if (this[key].setValues) {
          this[key].setValues(changedValues[key]);
        } else if (Array.isArray(this[key])) {
          this[key] = changedValues[key];
        } else if (changedValues[key] === undefined) {
          this[key] = undefined;
        } else {
          this[key] = { ...this[key], ...changedValues[key] };
        }
      } else {
        this[key] = changedValues[key];
      }
    }
    this.setHasChanges(true);
  }
  public setMunicipalityName(name: string) {
    const regEx = new RegExp(textValidationPattern);
    if (regEx.test(name)) this.municipalityName = name;
    this.setHasChanges(true);
  }
  public setMunicipalityLogo(logo?: IImage) {
    this.municipalityLogo = logo;
    this.setHasChanges(true);
  }
  public setMunicipalityIcon(icon?: IImage) {
    this.municipalityIcon = icon;
    this.setHasChanges(true);
  }
  public setServerInfos(serverInfos?: IServerInfo[]) {
    this.serverInfos = [];
    if (serverInfos) {
      this.serverInfos.push(...serverInfos);
    }
    this.setHasChanges(true);
  }
  public setLocators(locators?: ILocator[]) {
    this.locators = [];
    if (locators) {
      this.locators.push(...locators);
    }
    this.setHasChanges(true);
  }
  public getLocatorsForMap(t: TFunction) {
    return this.locators
      .filter((locator) => locator.useInMap)
      .map((locator) => {
        let urlName = locator.url.replace('/GeocodeServer', '');
        urlName = urlName.substr(urlName.lastIndexOf('/') + 1);

        const translatedName = t(
          `locator${locator.locatorType.charAt(0).toUpperCase() + locator.locatorType.slice(1)}`
        );

        return {
          url: locator.url,
          name: locator.locatorType !== LocatorType.Other ? translatedName : urlName
        };
      });
  }
  public setHideToken(setValue: boolean) {
    this.hideToken = setValue;
  }
  public updatePortalUrl(newUrl: string) {
    this.portalUrl = newUrl;
    this.setHasChanges(true);
  }
  public updateAppId(newAppId: string) {
    this.appId = newAppId;
    this.setHasChanges(true);
  }
  public setHasChanges(value: boolean) {
    this.hasChanges = value;
  }
  public toJSON(): IGeneralSettingsProperties {
    return toJS(this);
  }
}

export const ServerModel = types
  .model('ServerModel', {
    url: types.string,
    loginType: types.optional(types.enumeration([...Object.values(LoginType)]), LoginType.noLogin),
    username: types.optional(types.maybe(types.string), undefined, [null, undefined]),
    password: types.optional(types.maybe(types.string), undefined, [null, undefined]),
    token: types.optional(types.maybe(types.string), undefined, [null, undefined]),
    validationStatus: types.optional(
      types.enumeration(Object.values(ServerValidationStatus)),
      ServerValidationStatus.untouched
    )
  })
  .actions((self) => ({
    setValues(changedValues: any) {
      for (const key in changedValues) {
        self[key] = changedValues[key];
      }
    }
  }));

export const LocatorModel = types.model('LocatorModel', {
  url: types.string,
  locatorType: types.optional(types.enumeration(Object.values(LocatorType)), LocatorType.Other),
  useInMap: types.optional(types.boolean, true)
});

export const ImageModel = types.model('ImageModel', {
  type: types.maybe(types.string),
  content: types.maybe(types.string),
  name: types.maybe(types.string),
  uid: types.maybe(types.identifier),
  status: types.maybe(types.string)
});

export interface IGeneralSettingsModelInstance extends Instance<typeof GeneralSettingsModel> {}
export interface IGeneralSettingsModelSnapshotIn extends SnapshotIn<typeof GeneralSettingsModel> {}
export interface ILocatorModelInstance extends Instance<typeof LocatorModel> {}
export interface ILocatorModelSnapshotIn extends SnapshotIn<typeof LocatorModel> {}
export interface IServerModelInstance extends Instance<typeof ServerModel> {}
export interface IServerModelSnapshotIn extends SnapshotIn<typeof ServerModel> {}
export interface IImageModelInstance extends Instance<typeof ImageModel> {}
export interface IImageModelSnapshotIn extends SnapshotIn<typeof ImageModel> {}

export const GeneralSettingsModel = types
  .model('GeneralSettingsModel', {
    municipalityName: types.optional(types.maybe(types.string), undefined, [null, undefined]),
    municipalityIcon: types.optional(types.maybe(ImageModel), undefined, [null, undefined]),
    municipalityLogo: types.optional(types.maybe(ImageModel), undefined, [null, undefined]),
    serverInfos: types.array(ServerModel),
    locators: types.array(LocatorModel),
    portalUrl: types.optional(types.maybe(types.string), undefined, [null, undefined]),
    appId: types.optional(types.maybe(types.string), undefined, [null, undefined])
  })
  .volatile(() => ({
    hideToken: false,
    hasChanges: false
  }))
  .actions((self) => ({
    setValues(changedValues: Partial<IGeneralSettingsProperties>) {
      for (const key in changedValues) {
        if (typeof self[key] === 'object') {
          if (self[key].setValues) {
            self[key].setValues(changedValues[key]);
          } else if (Array.isArray(self[key])) {
            self[key] = changedValues[key];
          } else if (changedValues[key] === undefined) {
            self[key] = undefined;
          } else {
            self[key] = { ...self[key], ...changedValues[key] };
          }
        } else {
          self[key] = changedValues[key];
        }
      }
      this.setHasChanges(true);
    },
    setMunicipalityName(name: string) {
      const regEx = new RegExp(textValidationPattern);
      if (regEx.test(name)) self.municipalityName = cast(name);
      this.setHasChanges(true);
    },
    setMunicipalityLogo(logo?: IImage) {
      self.municipalityLogo = cast(logo);
      this.setHasChanges(true);
    },
    setMunicipalityIcon(icon?: IImage) {
      self.municipalityIcon = cast(icon);
      this.setHasChanges(true);
    },
    setServerInfos(serverInfos?: IServerInfo[]) {
      self.serverInfos.clear();
      if (serverInfos) {
        self.serverInfos.push(...serverInfos);
      }
      this.setHasChanges(true);
    },
    setLocators(locators?: ILocator[]) {
      self.locators.clear();
      if (locators) {
        self.locators.push(...locators);
      }
      this.setHasChanges(true);
    },
    getLocatorsForMap(t: TFunction) {
      return self.locators
        .filter((locator) => locator.useInMap)
        .map((locator) => {
          let urlName = locator.url.replace('/GeocodeServer', '');
          urlName = urlName.substring(urlName.lastIndexOf('/') + 1);

          const translatedName = t(
            `locator${locator.locatorType.charAt(0).toUpperCase() + locator.locatorType.slice(1)}`
          );

          return {
            url: locator.url,
            name: locator.locatorType !== LocatorType.Other ? translatedName : urlName
          };
        });
    },
    setHideToken(setValue: boolean) {
      self.hideToken = setValue;
    },
    updatePortalUrl(newUrl: string) {
      self.portalUrl = newUrl;
      this.setHasChanges(true);
    },
    updateAppId(newAppId: string) {
      self.appId = newAppId;
      this.setHasChanges(true);
    },
    setHasChanges(value: boolean) {
      self.hasChanges = value;
    }
  }))
  .views((self) => ({
    toJSON(): IGeneralSettingsProperties {
      return getSnapshot(self);
    }
  }));
